import { Link } from '@tanstack/react-router';
import { useActiveFleet } from '@/data/fleets';
import { useFirstRun } from '@/data/first-run';
import { SetupMeter } from '@/components/setup-meter';
import { cn } from '@/lib/cn';

const ITEMS: Record<
  'rates' | 'brand' | 'driver' | 'customer' | 'load',
  { label: string; hint: string; to: string }
> = {
  rates: {
    label: 'Confirm your rates',
    hint: 'The standard rate card is loaded — check the numbers are yours and save.',
    to: '/settings/rates',
  },
  brand: {
    label: 'Your brand',
    hint: 'Name is prefilled; add your colours or logo (optional but worth it).',
    to: '/settings/brand',
  },
  driver: {
    label: 'Add yourself as a driver with your vehicle',
    hint: 'Add a vehicle, then set it as your default — or invite your first driver.',
    to: '/vehicles',
  },
  customer: {
    label: 'Add or invite your first customer',
    hint: 'Create them in Customers, or send an invite to yourself to see their side.',
    to: '/customers',
  },
  load: {
    label: 'Book a test load end to end',
    hint: 'Create it, assign it, watch it on the portal and tracking page.',
    to: '/loads/new',
  },
};

/**
 * P1 first-run checklist — persistent on the home page for TRIAL fleets
 * until every item derives done. Never shown to non-trial fleets (Staith,
 * Harbour, anything hand-provisioned: trialEndsAt is null there).
 */
export function FirstRunCard() {
  const { fleet } = useActiveFleet();
  const isTrial = Boolean(fleet?.trialEndsAt);
  const firstRun = useFirstRun(fleet?.id ?? null, isTrial);

  if (!isTrial || !firstRun.data || firstRun.data.complete) return null;

  const doneCount = firstRun.data.items.filter((i) => i.done).length;

  return (
    <section className="surface rounded-lg p-5 mb-6 border-l-4 border-l-breaker-identity">
      {/* Setup-wizard unit: the wizard's meter continues here — one journey,
          now counting checklist items instead of flow steps. */}
      <SetupMeter
        label={`Setup — ${doneCount} of ${firstRun.data.items.length} done`}
        current={doneCount}
        total={firstRun.data.items.length}
        className="mb-3"
      />
      <ol className="space-y-2">
        {firstRun.data.items.map((item) => {
          const meta = ITEMS[item.key];
          return (
            <li key={item.key} className="flex items-start gap-3">
              <span
                aria-hidden
                className={cn(
                  'mt-0.5 inline-flex h-4 w-4 shrink-0 items-center justify-center rounded-full border text-[10px]',
                  item.done
                    ? 'bg-breaker-ink border-breaker-ink text-white'
                    : 'border-breaker-line-2 text-transparent',
                )}
              >
                ✓
              </span>
              <div className="min-w-0">
                <Link
                  to={meta.to}
                  className={cn(
                    'text-sm font-medium transition-colors',
                    item.done
                      ? 'text-breaker-mute line-through'
                      : 'text-breaker-ink hover:text-breaker-identity',
                  )}
                >
                  {meta.label}
                </Link>
                {!item.done ? (
                  <p className="text-xs text-breaker-mute">{meta.hint}</p>
                ) : null}
              </div>
            </li>
          );
        })}
      </ol>
    </section>
  );
}

/** "N days left" — countdown ONLY. Enforcement deliberately does not exist
 *  until the P2 billing rail lands; expiry changes nothing but this copy.
 *
 *  0084: a founding fleet is provisioned with a normal trial_ends_at it will
 *  never meet, so the countdown would be a lie — and a steadily more
 *  alarming one. The exemption replaces it outright. */
export function TrialBanner() {
  const { fleet } = useActiveFleet();
  if (fleet?.billingExempt) {
    return (
      <div className="bg-breaker-ground-2 border-b border-breaker-line px-4 py-1.5 text-center text-xs text-breaker-ink-2">
        Founding — no charge, everything switched on.
      </div>
    );
  }
  if (!fleet?.trialEndsAt) return null;
  const daysLeft = Math.max(
    0,
    Math.ceil((Date.parse(fleet.trialEndsAt) - Date.now()) / 86_400_000),
  );
  return (
    <div className="bg-breaker-ground-2 border-b border-breaker-line px-4 py-1.5 text-center text-xs text-breaker-ink-2">
      Trial — {daysLeft} day{daysLeft === 1 ? '' : 's'} left, everything switched on.
    </div>
  );
}
